import { Navigate, Outlet, Route, Routes, useLocation } from "react-router-dom";

import SignupPage from "@/pages/SignupPage";
import HomePage from "@/pages/HomePage";
import PortalLandingPage from "@/pages/PortalLandingPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordConfirmPage from "@/pages/ResetPasswordConfirmPage";
import VerifyEmailPage from "@/pages/VerifyEmailPage";
import AdminForgotPasswordPage from "@/pages/admin/AdminForgotPasswordPage";
import AdminHomePage from "@/pages/admin/AdminHomePage";
import AdminImportedIntegratorsPage from "@/pages/admin/AdminImportedIntegratorsPage";
import AdminLoginPage from "@/pages/admin/AdminLoginPage";
import AdminMetricsPage from "@/pages/admin/AdminMetricsPage";
import AdminResetPasswordConfirmPage from "@/pages/admin/AdminResetPasswordConfirmPage";
import AdminUsersPage from "@/pages/admin/AdminUsersPage";
import { useFeatureFlagsResolved } from "@/lib/feature-flags/useSandboxCredentialsEnabled";
import DocsFramePage from "@/pages/glofox/DocsFramePage";
import { GLOFOX_DOCS_HOME_ROUTE } from "@/lib/portal/docs-urls";
import { isDemoDocsEnabled } from "@/lib/portal/demo-docs-enabled";
import {
  GLOFOX_LEGACY_ACCESS_REQUEST_ROUTE,
  GLOFOX_REQUEST_ACCESS_ROUTE,
} from "@/lib/portal/glofox-routes";
import {
  PORTAL_HOME_ROUTE,
  PORTAL_LANDING_ROUTE,
  PORTAL_LOGIN_LEGACY_ROUTE,
  PORTAL_SIGNUP_LEGACY_ROUTE,
  PORTAL_SIGNUP_ROUTE,
} from "@/lib/portal/portal-routes";

/** Preserve search/state when rewriting legacy auth paths. */
function RedirectPreserve({ to }: { to: string }) {
  const location = useLocation();
  return (
    <Navigate
      to={{ pathname: to, search: location.search, hash: location.hash }}
      state={location.state}
      replace
    />
  );
}

function FlaggedLoginRoute() {
  return <RedirectPreserve to={PORTAL_HOME_ROUTE} />;
}

function FlaggedSignupRoute() {
  return <RedirectPreserve to={PORTAL_SIGNUP_ROUTE} />;
}

/** Keeps one DocsFramePage mounted across `/glofox` and `/glofox/*` child matches. */
function GlofoxSlice6DocsLayout() {
  return (
    <>
      <DocsFramePage />
      <Outlet />
    </>
  );
}

/** Other-org template: `#/demo` iframes `/api-portal-docs/demo/` (not nested under Glofox). */
function DemoDocsLayout() {
  return (
    <>
      <DocsFramePage />
      <Outlet />
    </>
  );
}

function FlaggedRootRoute() {
  return <Navigate to={PORTAL_LANDING_ROUTE} replace />;
}

function FlaggedPortalLandingRoute() {
  return <PortalLandingPage />;
}

export function AppRoutes() {
  const flagsResolved = useFeatureFlagsResolved();

  // Fail-closed flags look the same as "not evaluated yet". Wait so a
  // remembered session is not routed on those defaults.
  if (!flagsResolved) {
    return (
      <p className="sr-only" role="status">
        Loading
      </p>
    );
  }

  const glofoxSlice6DocRoutes = (
    <Route element={<GlofoxSlice6DocsLayout />}>
      <Route path="/glofox" />
      <Route path="/glofox/*" />
    </Route>
  );

  return (
    <Routes>
      <Route path="/" element={<FlaggedRootRoute />} />
      <Route
        path={PORTAL_LANDING_ROUTE}
        element={<FlaggedPortalLandingRoute />}
      />
      <Route path={PORTAL_HOME_ROUTE} element={<HomePage />} />
      <Route path={PORTAL_SIGNUP_ROUTE} element={<SignupPage />} />
      <Route
        path={PORTAL_LOGIN_LEGACY_ROUTE}
        element={<FlaggedLoginRoute />}
      />
      <Route
        path={PORTAL_SIGNUP_LEGACY_ROUTE}
        element={<FlaggedSignupRoute />}
      />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route
        path="/forgot-password/confirm"
        element={<ResetPasswordConfirmPage />}
      />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/admin/login" element={<AdminLoginPage />} />
      <Route
        path="/admin/forgot-password"
        element={<AdminForgotPasswordPage />}
      />
      <Route
        path="/admin/forgot-password/confirm"
        element={<AdminResetPasswordConfirmPage />}
      />
      <Route path="/admin/control-panel" element={<AdminHomePage />} />
      <Route path="/admin/metrics" element={<AdminMetricsPage />} />
      <Route path="/admin/users" element={<AdminUsersPage />} />
      <Route
        path="/admin/imported-integrators"
        element={<AdminImportedIntegratorsPage />}
      />
      <Route
        path="/admin"
        element={<Navigate to="/admin/control-panel" replace />}
      />
      <Route
        path="/admin/drawer"
        element={<Navigate to="/admin/control-panel" replace />}
      />
      {isDemoDocsEnabled() ? (
        <Route element={<DemoDocsLayout />}>
          <Route path="/demo" />
          <Route path="/demo/*" />
        </Route>
      ) : (
        <>
          <Route
            path="/demo"
            element={<Navigate to={GLOFOX_DOCS_HOME_ROUTE} replace />}
          />
          <Route
            path="/demo/*"
            element={<Navigate to={GLOFOX_DOCS_HOME_ROUTE} replace />}
          />
        </>
      )}
      <Route
        path={GLOFOX_LEGACY_ACCESS_REQUEST_ROUTE}
        element={<RedirectPreserve to={GLOFOX_REQUEST_ACCESS_ROUTE} />}
      />
      <Route
        path="/glofox/login"
        element={<FlaggedLoginRoute />}
      />
      <Route
        path="/glofox/signup"
        element={<FlaggedSignupRoute />}
      />
      <Route
        path="/glofox/sandbox"
        element={<Navigate to={PORTAL_HOME_ROUTE} replace />}
      />
      {glofoxSlice6DocRoutes}
    </Routes>
  );
}
